import { useEffect, useState } from 'react';
import { uniq } from 'lodash';
import { useError } from '../../providers/useError';
import { userService } from './userService';
import { UserPublicDto } from './userService.dto';

interface HasUserId {
  userId: number;
}

export interface DataWithPublicUser<T> {
  data: T;
  user: UserPublicDto;
}

export const useDataWithPublicUser = <T extends HasUserId>(sourceData: T[]) => {
  const [data, setData] = useState<DataWithPublicUser<T>[]>([]);
  const [usersCache, setUsersCache] = useState<UserPublicDto[]>([]);
  const [loading, setLoading] = useState(false);

  const { errorMessage } = useError();

  useEffect(() => {
    refresh();
  }, [sourceData.length]);

  const fetchData = async () => {
    if (!sourceData.length || loading) return;

    try {
      setLoading(true);
      const { data } = await fetchDataAndMergeUsers();

      setData(current => [...current, ...data]);
    } catch (error) {
      errorMessage();
    } finally {
      setLoading(false);
    }
  };

  const fetchDataAndMergeUsers = async () => {
    const idsFromPage = sourceData.map(row => row.userId);
    const idsInCache = usersCache.map(user => user.id);
    const idsToFetch = idsFromPage.filter(id => !idsInCache.includes(id));

    const allCachedUsers = await fetchAndCacheUsers(idsToFetch);
    const sourceDataWithUsers = sourceData.map(source => ({
      data: source,
      user: allCachedUsers.find(user => user.id === source.userId)!!,
    }));

    return { data: sourceDataWithUsers };
  };

  const fetchAndCacheUsers = async (idsToFetch: number[]) => {
    if (idsToFetch.length > 0) {
      const { data: users } = await userService.fetchPublicUsers(uniq(idsToFetch));
      const newUsersCache = [...usersCache, ...users];
      setUsersCache(newUsersCache);
      return newUsersCache;
    } else {
      return usersCache;
    }
  };

  const refresh = () => {
    setData([]);
    setLoading(false);

    fetchData();
  };

  return { data, fetchData, refresh, loading };
};
